import './MetricDropdown.scss';
import cn from 'classnames';
import {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import ReactDOM from 'react-dom';
import { MdSearch } from 'react-icons/md';
import { ValueLabelRecord } from '@blisspointmedia/bpm-types/dist/App2Performance';
import { colOrMetricRequiresKpi } from '@blisspointmedia/bpm-types/dist/Columns';
import { useFilteredList } from '@src/utils/search';
import { Button } from '../Button';
import { Appearance, Size } from '../designConstants';
import CaretIcon from '../Dropdown/CaretIcon.svg?react';
import { InteractiveDiv } from '../InteractiveEl';

export interface MetricConfig {
  metric: string;
  kpi: string | null;
  source?: string | null;
  lag?: string | null;
  /** Selected value from the metric's customValuePane, if any. */
  customValue?: string | null;
}

/** Which optional third pane to show (metric → kpi → this). Only one at a time. */
export type OptionalPaneType = 'source' | 'lag';

export interface MetricDropdownOption {
  label: string;
  value: string;
  /** Optional description rendered under the label (custom value pane only). */
  description?: string;
  disabled?: boolean;
  isCustomMetric?: boolean;
  isCustomMetricWithGlobalKpi?: boolean;
  /**
   * When set, clicking this metric opens a custom value pane instead of the KPI flow.
   * Mutually exclusive with isCustomMetric. Selected value emits as MetricConfig.customValue.
   */
  customValuePane?: {
    title: string;
    options: MetricDropdownOption[];
  };
}

interface MetricDropdownProps {
  value: MetricConfig | null;
  metrics: MetricDropdownOption[];
  kpis: MetricDropdownOption[];
  sources?: MetricDropdownOption[];
  lags?: MetricDropdownOption[];
  /** When set, shows the corresponding third pane. Use "source" or "lag", not both. */
  optionalPane?: OptionalPaneType;
  /**
   * When true, shows an additional Lag pane alongside the KPI pane. Unlike `optionalPane='lag'`,
   * this is composable with `optionalPane='source'` (so a metric can have both a source picker
   * AND a lag picker). When the pane is showing, a lag selection is required to apply (Apply
   * stays disabled until the user picks one). The lag pane is gated to performance metrics
   * (same condition as the KPI pane), so it does NOT appear for custom metrics without a global
   * KPI or for metrics that don't require a KPI. Ignored when `optionalPane === 'lag'` to avoid
   * double-rendering.
   */
  showLag?: boolean;
  onChange: (config: MetricConfig) => void;
  className?: string;
  menuClassName?: string;
  showPrettyKpiLabel?: boolean;
  closeOnScroll?: boolean;
  showSearchBar?: boolean;
  metricPaneTitle?: string;
  kpiPaneTitle?: string;
  optionalPaneTitle?: string;
}

const getMetricPrettyName = (metric: string | null, metrics: MetricDropdownOption[]) => {
  if (!metric) {
    return '--';
  }
  return metrics.find(el => el.value === metric)?.label || metric;
};

const getDisplayLabel = (
  config: MetricConfig | null,
  metrics: MetricDropdownOption[],
  options: {
    sources?: { label: string; value: string }[];
    lags?: { label: string; value: string }[];
    kpis?: ValueLabelRecord[];
    showPrettyKpiLabel?: boolean;
  } = {}
) => {
  if (!config) {
    return 'Select a metric';
  }

  const { sources = [], lags = [], kpis = [], showPrettyKpiLabel = false } = options;
  const metricPrettyName = getMetricPrettyName(config.metric, metrics);

  let label: string;
  if (config.customValue) {
    const metricOption = metrics.find(m => m.value === config.metric);
    const customOption = metricOption?.customValuePane?.options.find(
      o => o.value === config.customValue
    );
    label = customOption ? `${metricPrettyName}, ${customOption.label}` : metricPrettyName;
  } else if (config.kpi && showPrettyKpiLabel) {
    const kpiLabelConfig = kpis.find(k => k.value === config.kpi);
    if (kpiLabelConfig) {
      label = `${metricPrettyName}, ${kpiLabelConfig.label}`;
    } else {
      label = `${metricPrettyName}, ${config.kpi}`;
    }
  } else if (config.kpi) {
    label = `${metricPrettyName}, ${config.kpi}`;
  } else {
    label = metricPrettyName;
  }

  if (config.source && sources.length > 0) {
    const sourcePrettyName = sources.find(s => s.value === config.source)?.label || config.source;
    label += ` (Source: ${sourcePrettyName})`;
  }
  if (config.lag && lags.length > 0) {
    const lagPrettyName = lags.find(l => l.value === config.lag)?.label || config.lag;
    label += ` (Lag: ${lagPrettyName})`;
  }

  return label;
};

const OPTIONAL_PANE_META: Record<OptionalPaneType, { title: string; key: 'source' | 'lag' }> = {
  source: { title: 'Source', key: 'source' },
  lag: { title: 'Lag', key: 'lag' }
};

export const MetricDropdown = ({
  value,
  metrics,
  kpis,
  sources = [],
  lags = [],
  optionalPane,
  showLag = false,
  onChange,
  className = '',
  menuClassName,
  showPrettyKpiLabel = false,
  closeOnScroll = false,
  showSearchBar = true,
  metricPaneTitle = 'Metric',
  kpiPaneTitle = 'KPI',
  optionalPaneTitle
}: MetricDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({
    position: 'fixed',
    visibility: 'hidden'
  });

  // Pending selections for the multi-pane flow
  const [pendingMetric, setPendingMetric] = useState<string | null>(null);
  const [pendingKpi, setPendingKpi] = useState<string | null>(null);
  const [pendingSource, setPendingSource] = useState<string | null>(null);
  const [pendingLag, setPendingLag] = useState<string | null>(null);
  const [pendingCustomValue, setPendingCustomValue] = useState<string | null>(null);

  // Search filters
  const [metricSearch, setMetricSearch] = useState('');
  const [kpiSearch, setKpiSearch] = useState('');

  const toggleRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredMetrics = useFilteredList(metrics, metricSearch, { keys: ['label'] });

  const standardMetrics = useMemo(
    () => filteredMetrics.filter(m => !m.isCustomMetric),
    [filteredMetrics]
  );

  const customMetrics = useMemo(
    () => filteredMetrics.filter(m => m.isCustomMetric),
    [filteredMetrics]
  );

  const filteredKpis = useFilteredList(kpis, kpiSearch, { keys: ['label'] });

  const metricRequiresKpi = useCallback(
    (metricValue: string): boolean => {
      const option = metrics.find(m => m.value === metricValue);
      if (option?.isCustomMetric) {
        return option.isCustomMetricWithGlobalKpi === true;
      }
      return colOrMetricRequiresKpi(metricValue);
    },
    [metrics]
  );

  const pendingMetricRequiresKpi = pendingMetric ? metricRequiresKpi(pendingMetric) : false;
  const pendingMetricIsCustomWithoutKpi = pendingMetric
    ? (() => {
        const option = metrics.find(m => m.value === pendingMetric);
        return option?.isCustomMetric === true && !option?.isCustomMetricWithGlobalKpi;
      })()
    : false;

  const pendingOption = pendingMetric ? metrics.find(m => m.value === pendingMetric) : null;
  const showCustomValuePane = Boolean(pendingOption?.customValuePane);

  const showKpiPane = pendingMetric !== null && pendingMetricRequiresKpi && !showCustomValuePane;
  const optionalPaneMeta = optionalPane ? OPTIONAL_PANE_META[optionalPane] : null;
  let optionalOptions: MetricDropdownOption[] = [];
  if (optionalPane === 'source') {
    optionalOptions = sources;
  } else if (optionalPane === 'lag') {
    optionalOptions = lags;
  }

  const showOptionalPane =
    Boolean(optionalPaneMeta) &&
    optionalOptions.length > 0 &&
    (showKpiPane || pendingMetricIsCustomWithoutKpi) &&
    !showCustomValuePane;

  // A composable Lag pane that can render alongside `optionalPane='source'` (so a metric config
  // can have both source AND lag pickers). Gated to performance metrics (same condition as the
  // KPI pane), per the per-metric-lag design. Skipped when `optionalPane === 'lag'` is already
  // doing the job to avoid double-rendering.
  const showSeparateLagPane =
    showLag && lags.length > 0 && showKpiPane && !showCustomValuePane && optionalPane !== 'lag';

  let pendingOptionalValue: string | null = null;
  if (optionalPane === 'source') {
    pendingOptionalValue = pendingSource;
  } else if (optionalPane === 'lag') {
    pendingOptionalValue = pendingLag;
  }

  let setPendingOptionalValue: (value: string | null) => void = () => {};
  if (optionalPane === 'source') {
    setPendingOptionalValue = setPendingSource;
  } else if (optionalPane === 'lag') {
    setPendingOptionalValue = setPendingLag;
  }

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setPendingMetric(null);
    setPendingKpi(null);
    setPendingSource(null);
    setPendingLag(null);
    setPendingCustomValue(null);
    setMetricSearch('');
    setKpiSearch('');
  }, [
    setIsOpen,
    setPendingMetric,
    setPendingKpi,
    setPendingSource,
    setPendingLag,
    setPendingCustomValue,
    setMetricSearch,
    setKpiSearch
  ]);

  /**
   * Updates the position of the dropdown menu based on the toggle button's position.
   * Uses fixed positioning with viewport-relative coordinates so it works correctly
   * with inner scroll containers (where window.scrollY is always 0).
   */
  const updateMenuPosition = () => {
    if (toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      const SPACING = 4;
      const VIEWPORT_PADDING = 8;

      // Use actual rendered height so the above/below decision and placement are accurate.
      // Fall back to a large number so we prefer opening below when height isn't known yet.
      const menuHeight = dropdownRef.current ? dropdownRef.current.offsetHeight : 9999;
      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldOpenAbove = spaceBelow < menuHeight;

      let { left } = rect;

      // Prevent the menu from overflowing the right edge of the viewport
      if (dropdownRef.current) {
        const menuWidth = dropdownRef.current.offsetWidth;
        const maxLeft = window.innerWidth - menuWidth - VIEWPORT_PADDING;
        if (left > maxLeft) {
          left = Math.max(VIEWPORT_PADDING, maxLeft);
        }
      }

      setMenuStyle({
        top: shouldOpenAbove ? rect.top - menuHeight - SPACING : rect.bottom + SPACING,
        left,
        position: 'fixed'
      });
    }
  };

  useLayoutEffect(() => {
    if (isOpen) {
      updateMenuPosition();
    }
  }, [
    isOpen,
    showKpiPane,
    showOptionalPane,
    showSeparateLagPane,
    showCustomValuePane,
    pendingMetricIsCustomWithoutKpi
  ]);

  useEffect(() => {
    if (!isOpen) return undefined;

    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', closeOnScroll ? closeDropdown : updateMenuPosition, true);

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setPendingMetric(null);
        setPendingKpi(null);
        setPendingSource(null);
        setPendingLag(null);
        setPendingCustomValue(null);
        setMetricSearch('');
        setKpiSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener(
        'scroll',
        closeOnScroll ? closeDropdown : updateMenuPosition,
        true
      );
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeDropdown, closeOnScroll]);

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    if (newIsOpen) {
      setIsOpen(true);
      if (value) {
        const option = metrics.find(m => m.value === value.metric);
        if (option?.customValuePane) {
          setPendingMetric(value.metric);
          setPendingCustomValue(value.customValue ?? null);
        } else if (metricRequiresKpi(value.metric) || option?.isCustomMetric) {
          setPendingMetric(value.metric);
          setPendingKpi(value.kpi ?? null);
          setPendingSource(value.source ?? null);
          setPendingLag(value.lag ?? null);
        }
      }
    } else {
      closeDropdown();
    }
  };

  const handleMetricClick = (metricValue: string) => {
    const requiresKpi = metricRequiresKpi(metricValue);
    const option = metrics.find(m => m.value === metricValue);

    if (requiresKpi) {
      setKpiSearch('');
    }

    if (option?.customValuePane) {
      setPendingMetric(metricValue);
      setPendingKpi(null);
      setPendingSource(null);
      setPendingLag(null);
      setPendingCustomValue(null);
    } else if (requiresKpi || option?.isCustomMetric) {
      setPendingMetric(metricValue);
      setPendingKpi(null);
      setPendingSource(null);
      setPendingLag(null);
    } else {
      onChange({ metric: metricValue, kpi: null });
      closeDropdown();
    }
  };

  const handleKpiClick = (kpiValue: string) => {
    setPendingKpi(kpiValue);
  };

  const handleOptionalValueClick = (optionValue: string) => {
    setPendingOptionalValue(optionValue);
  };

  const handleApply = () => {
    if (showCustomValuePane) {
      if (pendingMetric && pendingCustomValue) {
        onChange({ metric: pendingMetric, kpi: null, customValue: pendingCustomValue });
        closeDropdown();
      }
      return;
    }
    if (pendingMetric && (!pendingMetricRequiresKpi || pendingKpi)) {
      const config: MetricConfig = {
        metric: pendingMetric,
        kpi: pendingKpi
      };
      if (optionalPane === 'source') {
        config.source = pendingSource ?? undefined;
      }
      if (optionalPane === 'lag') {
        config.lag = pendingLag ?? undefined;
      }
      if (showSeparateLagPane) {
        config.lag = pendingLag ?? undefined;
      }
      onChange(config);
      closeDropdown();
    }
  };

  const handleCancel = () => {
    closeDropdown();
  };

  const canApply = showCustomValuePane
    ? Boolean(pendingMetric && pendingCustomValue)
    : Boolean(
        pendingMetric &&
        (!pendingMetricRequiresKpi || pendingKpi) &&
        (!showOptionalPane || pendingOptionalValue) &&
        (!showSeparateLagPane || pendingLag)
      );

  const dropdownToggleLabel = getDisplayLabel(value, metrics, {
    sources,
    lags,
    kpis,
    showPrettyKpiLabel
  });

  const dropdownMenu = (
    <div
      ref={dropdownRef}
      className={cn('metric-dropdown-menu', menuClassName)}
      role='presentation'
      style={menuStyle}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className='metric-dropdown-panes'>
        {/* Metrics Pane */}
        <div className='metric-dropdown-pane'>
          <div className='pane-header'>{metricPaneTitle}</div>
          {showSearchBar && (
            <div className='pane-search'>
              <MdSearch className='pane-search-icon' />
              <input
                type='text'
                placeholder='Search Metrics'
                value={metricSearch}
                onChange={e => setMetricSearch(e.target.value)}
              />
            </div>
          )}
          <div className='pane-items'>
            {customMetrics.length > 0 && (
              <>
                <div className='pane-section-header'>Custom Metrics</div>
                {customMetrics.map(metric => {
                  const { label, value: metricValue, disabled } = metric;
                  return (
                    <InteractiveDiv
                      key={metricValue}
                      className={cn('metric-dropdown-item', {
                        selected: pendingMetric === metricValue,
                        disabled
                      })}
                      disabled={disabled}
                      onClick={() => handleMetricClick(metricValue)}
                    >
                      <span>{label}</span>
                    </InteractiveDiv>
                  );
                })}
              </>
            )}
            <div className='pane-section-header'>Standard Metrics</div>
            {standardMetrics.map(metric => {
              const { label, value: metricValue, disabled } = metric;
              return (
                <InteractiveDiv
                  key={metricValue}
                  className={cn('metric-dropdown-item', {
                    selected: pendingMetric === metricValue,
                    disabled
                  })}
                  disabled={disabled}
                  onClick={() => handleMetricClick(metricValue)}
                >
                  <span>{label}</span>
                </InteractiveDiv>
              );
            })}
          </div>
        </div>

        {/* KPI Pane */}
        {showKpiPane && (
          <div className='metric-dropdown-pane'>
            <div className='pane-header'>{kpiPaneTitle}</div>
            {showSearchBar && (
              <div className='pane-search'>
                <MdSearch className='pane-search-icon' />
                <input
                  type='text'
                  placeholder='Search KPIs'
                  value={kpiSearch}
                  onChange={e => setKpiSearch(e.target.value)}
                />
              </div>
            )}
            <div className='pane-items'>
              {filteredKpis.map(kpi => {
                const { label, value: kpiValue, disabled } = kpi;
                return (
                  <InteractiveDiv
                    key={kpiValue}
                    className={cn('metric-dropdown-item', {
                      selected: pendingKpi === kpiValue,
                      disabled
                    })}
                    disabled={disabled}
                    onClick={() => handleKpiClick(kpiValue)}
                  >
                    <span>{label}</span>
                  </InteractiveDiv>
                );
              })}
            </div>
          </div>
        )}
        {showCustomValuePane && pendingOption?.customValuePane && (
          <div className='metric-dropdown-pane'>
            <div className='pane-header'>{pendingOption.customValuePane.title}</div>
            <div className='pane-items'>
              {pendingOption.customValuePane.options.map(opt => (
                <InteractiveDiv
                  key={opt.value}
                  className={cn('metric-dropdown-item', {
                    selected: pendingCustomValue === opt.value,
                    disabled: Boolean(opt.disabled),
                    'has-description': Boolean(opt.description)
                  })}
                  disabled={Boolean(opt.disabled)}
                  onClick={() => setPendingCustomValue(opt.value)}
                >
                  <span className='metric-dropdown-item-label'>{opt.label}</span>
                  {opt.description && (
                    <span className='metric-dropdown-item-description'>{opt.description}</span>
                  )}
                </InteractiveDiv>
              ))}
            </div>
          </div>
        )}

        {/* Optional third pane (Source, Lag, etc.) */}
        {showOptionalPane && optionalPaneMeta && (
          <div className='metric-dropdown-pane'>
            <div className='pane-header'>{optionalPaneTitle ?? optionalPaneMeta.title}</div>
            <div className='pane-items'>
              {optionalOptions.map(opt => {
                const { label, value: optValue, disabled } = opt;
                return (
                  <InteractiveDiv
                    key={optValue}
                    className={cn('metric-dropdown-item', {
                      selected: pendingOptionalValue === optValue,
                      disabled
                    })}
                    disabled={disabled}
                    onClick={() => handleOptionalValueClick(optValue)}
                  >
                    <span>{label}</span>
                  </InteractiveDiv>
                );
              })}
            </div>
          </div>
        )}

        {/* Composable Lag pane (independent of `optionalPane`). Only renders for performance
            metrics — same condition as the KPI pane. Selection is optional. */}
        {showSeparateLagPane && (
          <div className='metric-dropdown-pane'>
            <div className='pane-header'>Lag</div>
            <div className='pane-items'>
              {lags.map(opt => {
                const { label, value: optValue, disabled } = opt;
                return (
                  <InteractiveDiv
                    key={optValue}
                    className={cn('metric-dropdown-item', {
                      selected: pendingLag === optValue,
                      disabled
                    })}
                    disabled={disabled}
                    onClick={() => setPendingLag(optValue)}
                  >
                    <span>{label}</span>
                  </InteractiveDiv>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Cancel / Apply actions - shown in multi-pane mode */}
      {(showKpiPane || pendingMetricIsCustomWithoutKpi || showCustomValuePane) && (
        <div className='metric-dropdown-actions'>
          <Button
            size={Size.LG}
            className='metric-dropdown-btn cancel'
            appearance={Appearance.OUTLINED}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            size={Size.LG}
            className={cn('metric-dropdown-btn apply', { disabled: !canApply })}
            onClick={handleApply}
            disabled={!canApply}
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className={cn('metric-selector', className)}>
      <InteractiveDiv ref={toggleRef} className='metric-dropdown-toggle' onClick={handleToggle}>
        <div
          className='metric-dropdown-toggle-label'
          title={dropdownToggleLabel}
          onMouseEnter={e => {
            const el = e.currentTarget;
            el.title = el.scrollWidth > el.clientWidth ? dropdownToggleLabel : '';
          }}
        >
          {dropdownToggleLabel}
        </div>
        <div className={cn({ 'caret-down': isOpen })}>
          <CaretIcon />
        </div>
      </InteractiveDiv>

      {isOpen && ReactDOM.createPortal(dropdownMenu, document.body)}
    </div>
  );
};
